import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const EmailReceiptTracker = ({ receipts, onRetry, loading }) => {
  const [filterStatus, setFilterStatus] = useState('all');

  const getStatusIcon = (status) => {
    const icons = {
      pending: 'Clock',
      sent: 'Send',
      delivered: 'CheckCircle',
      failed: 'XCircle',
      bounced: 'AlertTriangle'
    };
    return icons?.[status] || 'Circle';
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'text-warning',
      sent: 'text-info',
      delivered: 'text-success',
      failed: 'text-destructive',
      bounced: 'text-destructive'
    };
    return colors?.[status] || 'text-muted-foreground';
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-warning/10 text-warning',
      sent: 'bg-info/10 text-info',
      delivered: 'bg-success/10 text-success',
      failed: 'bg-destructive/10 text-destructive',
      bounced: 'bg-destructive/10 text-destructive'
    };
    return badges?.[status] || 'bg-muted text-muted-foreground';
  };

  const getTypeLabel = (type) => {
    const labels = {
      pos_sale: 'POS Receipt',
      membership_payment: 'Payment Confirmation',
      invoice: 'Invoice'
    };
    return labels?.[type] || type;
  };

  const filteredReceipts = filterStatus === 'all'
    ? receipts
    : receipts?.filter(r => r?.email_status === filterStatus);

  const statusCounts = {
    all: receipts?.length || 0,
    sent: receipts?.filter(r => r?.email_status === 'sent')?.length || 0,
    delivered: receipts?.filter(r => r?.email_status === 'delivered')?.length || 0,
    failed: receipts?.filter(r => r?.email_status === 'failed')?.length || 0
  };

  if (loading) {
    return (
      <div className="bg-card rounded-xl shadow-md p-6">
        <div className="flex items-center justify-center h-48">
          <Icon name="Loader2" className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Email Receipt Tracker</h3>
          <p className="text-sm text-muted-foreground">Monitor automated email delivery status</p>
        </div>
      </div>

      {/* Status Filter Tabs */}
      <div className="flex items-center gap-2 mb-6 overflow-x-auto">
        {[
          { value: 'all', label: 'All', count: statusCounts?.all },
          { value: 'sent', label: 'Sent', count: statusCounts?.sent },
          { value: 'delivered', label: 'Delivered', count: statusCounts?.delivered },
          { value: 'failed', label: 'Failed', count: statusCounts?.failed }
        ]?.map((tab) => (
          <button
            key={tab?.value}
            onClick={() => setFilterStatus(tab?.value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              filterStatus === tab?.value
                ? 'bg-primary text-primary-foreground shadow-md'
                : 'bg-accent text-muted-foreground hover:bg-accent/80'
            }`}
          >
            {tab?.label} ({tab?.count})
          </button>
        ))}
      </div>

      <div className="space-y-3 max-h-[500px] overflow-y-auto">
        {filteredReceipts?.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Icon name="Mail" size={48} className="mx-auto mb-3" />
            <p>No email receipts found</p>
          </div>
        ) : (
          filteredReceipts?.map((receipt) => (
            <div key={receipt?.id} className="border border-border rounded-lg p-4 hover:shadow-sm transition-all">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1">
                  <div className={`w-10 h-10 rounded-full bg-accent flex items-center justify-center ${getStatusColor(receipt?.email_status)}`}>
                    <Icon name={getStatusIcon(receipt?.email_status)} size={20} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-foreground">{receipt?.transaction_number}</h4>
                      <span className={`text-xs px-2 py-1 rounded-full ${getStatusBadge(receipt?.email_status)}`}>
                        {receipt?.email_status?.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      {getTypeLabel(receipt?.transaction_type)} • {receipt?.recipient_email}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span>Sent: {receipt?.sent_at ? new Date(receipt?.sent_at)?.toLocaleString() : 'Pending'}</span>
                      {receipt?.retry_count > 0 && (
                        <span className="text-warning">Retries: {receipt?.retry_count}</span>
                      )}
                    </div>
                    {receipt?.failed_reason && (
                      <div className="mt-2 p-2 bg-destructive/5 border border-destructive/20 rounded text-xs text-destructive">
                        <Icon name="AlertCircle" size={12} className="inline mr-1" />
                        {receipt?.failed_reason}
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  {receipt?.email_status === 'failed' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onRetry?.(receipt?.id)}
                      iconName="RefreshCw"
                      iconPosition="left"
                    >
                      Retry
                    </Button>
                  )}
                  {receipt?.email_status === 'delivered' && (
                    <div className="flex items-center gap-1 text-success text-sm">
                      <Icon name="CheckCircle" size={16} />
                      <span>Delivered</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default EmailReceiptTracker;